// The arithmetic behind the Baptism register's summary. Pure functions over the
// three sacrament registers (baptism, first communion, confirmation) and the
// member directory, so the screen only lays the answers out.

import { dateOf, inYear, lastMonths, recent, isoOf } from '../../utils/summaryStats';
import { liveByStatus } from '../../utils/records';

export interface BaptismLike {
  recordId?: string;
  status?: string;
  memberId?: string;
  childName?: string;
  dob?: string;
  baptismDate?: string;
  minister?: string;
  registerNo?: string;
  certificateNo?: string;
}

export interface CommunionLike {
  recordId?: string;
  status?: string;
  memberId?: string;
  personName?: string;
  communionDate?: string;
  minister?: string;
  communionRegisterNo?: string;
  certificateNo?: string;
}

export interface ConfirmationLike {
  recordId?: string;
  status?: string;
  memberId?: string;
  personName?: string;
  confirmationDate?: string;
  bishopName?: string;
  confirmationRegisterNo?: string;
  certificateNo?: string;
}

/** Just what the "who is due" rule reads off a member. */
export interface SacramentMemberLike {
  memberId?: string;
  name?: string;
  status?: string;
  dateOfBirth?: string;
  baptismDate?: string;
  communionDate?: string;
  communionStatus?: string;
  confirmationDate?: string;
}

export interface DueLimits {
  baptismDueMonths: number;
  communionDueAge: number;
  confirmationDueAge: number;
}

export type SacramentKind = 'Baptism' | 'Communion' | 'Confirmation';

/** One register entry, whichever register it came from, in a common shape. */
export interface SacramentEntry {
  kind: SacramentKind;
  recordId: string;
  name: string;
  memberId: string;
  date: string;
  officiant: string;
  registerNo: string;
  certificateNo: string;
}

/** Baptism / communion / confirmation counts for one bucket (a month, a year, a minister). */
export interface SacramentSplit {
  key: string;
  label: string;
  baptism: number;
  communion: number;
  confirmation: number;
  total: number;
}

export interface MissingNumbers extends SacramentEntry {
  missing: string[];
}

export interface DueMember {
  memberId: string;
  name: string;
  ageYears: number | null;
  stages: SacramentKind[];
}

export interface BaptismSummary {
  totals: { baptism: number; communion: number; confirmation: number; all: number };
  thisYear: { baptism: number; communion: number; confirmation: number; all: number };
  lastYear: { baptism: number; communion: number; confirmation: number; all: number };
  months: SacramentSplit[];
  years: SacramentSplit[];
  officiants: SacramentSplit[];
  recent: SacramentEntry[];
  missingNumbers: MissingNumbers[];
  notLinked: SacramentEntry[];
  /** Average age in months of children at baptism, over entries with both dates. */
  avgBaptismAgeMonths: number | null;
  due: { baptism: number; communion: number; confirmation: number; members: DueMember[] };
}

const txt = (v: unknown): string => String(v ?? '').trim();

export const toEntries = (
  baptisms: BaptismLike[], communions: CommunionLike[], confirmations: ConfirmationLike[]
): SacramentEntry[] => [
  ...liveByStatus(baptisms).map((b) => ({
    kind: 'Baptism' as SacramentKind, recordId: txt(b.recordId), name: txt(b.childName), memberId: txt(b.memberId),
    date: dateOf(b.baptismDate) || '', officiant: txt(b.minister),
    registerNo: txt(b.registerNo), certificateNo: txt(b.certificateNo)
  })),
  ...liveByStatus(communions).map((c) => ({
    kind: 'Communion' as SacramentKind, recordId: txt(c.recordId), name: txt(c.personName), memberId: txt(c.memberId),
    date: dateOf(c.communionDate) || '', officiant: txt(c.minister),
    registerNo: txt(c.communionRegisterNo), certificateNo: txt(c.certificateNo)
  })),
  ...liveByStatus(confirmations).map((c) => ({
    kind: 'Confirmation' as SacramentKind, recordId: txt(c.recordId), name: txt(c.personName), memberId: txt(c.memberId),
    date: dateOf(c.confirmationDate) || '', officiant: txt(c.bishopName),
    registerNo: txt(c.confirmationRegisterNo), certificateNo: txt(c.certificateNo)
  }))
];

const tally = (rows: SacramentEntry[]) => {
  const out = { baptism: 0, communion: 0, confirmation: 0, all: rows.length };
  rows.forEach((r) => {
    if (r.kind === 'Baptism') out.baptism += 1;
    else if (r.kind === 'Communion') out.communion += 1;
    else out.confirmation += 1;
  });
  return out;
};

const split = (key: string, label: string, rows: SacramentEntry[]): SacramentSplit => {
  const c = tally(rows);
  return { key, label, baptism: c.baptism, communion: c.communion, confirmation: c.confirmation, total: c.all };
};

/** Entries grouped by a key, largest total first (ties by label). */
const splitBy = (rows: SacramentEntry[], pick: (r: SacramentEntry) => string, blank: string): SacramentSplit[] => {
  const map = new Map<string, SacramentEntry[]>();
  rows.forEach((r) => {
    const k = pick(r) || blank;
    map.set(k, [...(map.get(k) || []), r]);
  });
  return Array.from(map, ([k, list]) => split(k, k, list))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
};

const MONTHS_PER_YEAR = 12;

/** Whole months between two ISO dates, or null. */
const monthsBetween = (from?: string, to?: string): number | null => {
  const a = dateOf(from);
  const b = dateOf(to);
  if (!a || !b) return null;
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  let months = (by - ay) * MONTHS_PER_YEAR + (bm - am);
  if (bd < ad) months -= 1;
  return months;
};

const has = (v?: string): boolean => Boolean(v && v.trim());

/**
 * The same rule as Members › Sacraments: a stage is due once the member is old
 * enough and has no record of it. Unknown age is never due; a member who has
 * died or transferred out is not waiting for anything.
 */
export const sacramentsDue = (
  members: SacramentMemberLike[], limits: DueLimits, now: Date = new Date()
): DueMember[] => {
  const today = isoOf(now);
  return members
    .filter((m) => m.status !== 'Deceased' && m.status !== 'Transferred' && m.status !== 'Inactive')
    .map((m) => {
      const ageMonths = monthsBetween(m.dateOfBirth, today);
      const oldEnough = (months: number) => ageMonths !== null && ageMonths >= months;
      const stages: SacramentKind[] = [];
      if (!has(m.baptismDate) && oldEnough(limits.baptismDueMonths)) stages.push('Baptism');
      if (!has(m.communionDate) && m.communionStatus !== 'Received' && oldEnough(limits.communionDueAge * MONTHS_PER_YEAR)) {
        stages.push('Communion');
      }
      if (!has(m.confirmationDate) && oldEnough(limits.confirmationDueAge * MONTHS_PER_YEAR)) stages.push('Confirmation');
      return {
        memberId: txt(m.memberId), name: txt(m.name),
        ageYears: ageMonths === null ? null : Math.floor(ageMonths / MONTHS_PER_YEAR), stages
      };
    })
    .filter((m) => m.stages.length > 0)
    .sort((a, b) => (b.ageYears ?? -1) - (a.ageYears ?? -1) || a.name.localeCompare(b.name));
};

export const baptismSummary = (
  baptisms: BaptismLike[],
  communions: CommunionLike[],
  confirmations: ConfirmationLike[],
  members: SacramentMemberLike[] = [],
  limits: DueLimits = { baptismDueMonths: 12, communionDueAge: 13, confirmationDueAge: 13 },
  now: Date = new Date()
): BaptismSummary => {
  const entries = toEntries(baptisms, communions, confirmations);
  const year = now.getFullYear();
  const today = isoOf(now);
  const dated = entries.filter((e) => e.date && e.date <= today);

  // One 12-month window per register, zipped into one row per month.
  const monthly = (kind: SacramentKind) => lastMonths(dated.filter((e) => e.kind === kind), (e) => e.date, 12, now);
  const [mb, mc, mf] = [monthly('Baptism'), monthly('Communion'), monthly('Confirmation')];
  const months = mb.map((m, i) => ({
    key: m.month, label: m.label,
    baptism: m.count, communion: mc[i].count, confirmation: mf[i].count,
    total: m.count + mc[i].count + mf[i].count
  }));

  const years = splitBy(entries.filter((e) => e.date), (e) => e.date.slice(0, 4), '')
    .sort((a, b) => b.key.localeCompare(a.key))
    .slice(0, 10);

  const ages = liveByStatus(baptisms)
    .map((b) => monthsBetween(b.dob, b.baptismDate))
    .filter((m): m is number => m !== null && m >= 0);

  const due = sacramentsDue(members, limits, now);

  return {
    totals: tally(entries),
    thisYear: tally(inYear(entries, (e) => e.date, year)),
    lastYear: tally(inYear(entries, (e) => e.date, year - 1)),
    months,
    years,
    officiants: splitBy(entries, (e) => e.officiant, 'Not recorded'),
    recent: recent(entries, (e) => e.date, 90, now),
    missingNumbers: entries
      .map((e) => ({
        ...e,
        missing: [!e.registerNo && 'Register No.', !e.certificateNo && 'Certificate No.'].filter(Boolean) as string[]
      }))
      .filter((e) => e.missing.length > 0)
      .sort((a, b) => b.date.localeCompare(a.date)),
    notLinked: entries.filter((e) => !e.memberId).sort((a, b) => b.date.localeCompare(a.date)),
    avgBaptismAgeMonths: ages.length ? Math.round(ages.reduce((n, m) => n + m, 0) / ages.length) : null,
    due: {
      baptism: due.filter((d) => d.stages.includes('Baptism')).length,
      communion: due.filter((d) => d.stages.includes('Communion')).length,
      confirmation: due.filter((d) => d.stages.includes('Confirmation')).length,
      members: due
    }
  };
};
